import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import CookieNotice from "./components/CookieNotice";
import YandexMetrika from "./components/YandexMetrika";
import ErrorBoundary from "./components/ErrorBoundary";
import UtmPersistence from "./components/UtmPersistence";

const NotFound = lazy(() => import("./pages/NotFound"));
const ResearchTochkaShozhdeniya = lazy(() => import("./pages/ResearchTochkaShozhdeniya"));
const ResearchTochkaShozhdeniyaSverka = lazy(() => import("./pages/ResearchTochkaShozhdeniyaSverka"));
const ResearchTikhayaZamena = lazy(() => import("./pages/ResearchTikhayaZamena"));
const ResearchAI20252026 = lazy(() => import("./pages/ResearchAI20252026"));
const Education = lazy(() => import("./pages/Education"));
const CourseCatalog = lazy(() => import("./pages/CourseCatalog"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const PersonalAI = lazy(() => import("./pages/PersonalAI"));
const Blog = lazy(() => import("./pages/Blog"));
const BusinessArticle = lazy(() => import("./pages/BusinessArticle"));
const Cases = lazy(() => import("./pages/Cases"));
const Team = lazy(() => import("./pages/Team"));
const IgorNikitin = lazy(() => import("./pages/IgorNikitin"));
const AndreyMaingardt = lazy(() => import("./pages/AndreyMaingardt"));
const YuliaBoltutskaya = lazy(() => import("./pages/YuliaBoltutskaya"));
// Dev-лаборатория вариантов блока ценности — только для разработки
const ValueBlockLab = import.meta.env.DEV
  ? lazy(() => import("./pages/dev/ValueBlockLab"))
  : null;
// Dev-лаборатория вариантов SEO-подвала — только для разработки
const SeoBlockLab = import.meta.env.DEV
  ? lazy(() => import("./pages/dev/SeoBlockLab"))
  : null;

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <UtmPersistence />
        <YandexMetrika />
        <ErrorBoundary
          fallback={
            <div className="min-h-screen flex items-center justify-center p-6 text-center">
              <div>
                <h1 className="text-2xl font-heading mb-3">Что-то пошло не так</h1>
                <p className="text-muted-foreground mb-4">Попробуйте обновить страницу.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-heading"
                >
                  Обновить
                </button>
              </div>
            </div>
          }
        >
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/research/tochka-shozhdeniya" element={<ResearchTochkaShozhdeniya />} />
              <Route path="/research/tochka-shozhdeniya-pervaya-sverka" element={<ResearchTochkaShozhdeniyaSverka />} />
              <Route path="/research/tikhaya-zamena" element={<ResearchTikhayaZamena />} />
              <Route path="/research/ai-2025-2026" element={<ResearchAI20252026 />} />
              <Route path="/education" element={<Education />} />
              <Route path="/education/courses" element={<CourseCatalog />} />
              <Route path="/education/courses/:slug" element={<CourseDetail />} />
              <Route path="/courses/:slug" element={<CourseDetail />} />
              <Route path="/education/personal-ai" element={<PersonalAI />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BusinessArticle />} />
              <Route path="/cases" element={<Cases />} />
              <Route path="/team" element={<Team />} />
              <Route path="/team/igor_nikitin" element={<IgorNikitin />} />
              <Route path="/andrey_meinhardt" element={<AndreyMaingardt />} />
              <Route path="/team/yulia_boltutskaya" element={<YuliaBoltutskaya />} />
              <Route path="/team/andrey_maingardt" element={<Navigate to="/andrey_meinhardt" replace />} />
              {ValueBlockLab && (
                <Route path="/dev/value-lab" element={<ValueBlockLab />} />
              )}
              {SeoBlockLab && (
                <Route path="/dev/seo-lab" element={<SeoBlockLab />} />
              )}
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <CookieNotice />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
